import { createAsyncThunk } from '@/redux/createAsyncThunk';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { fetchItemSummaries } from '@/redux/modules/item/summary/itemSummary.actions';
import { fetchDataForEndedItemPage } from '@/redux/modules/itemPage/endedItemPage.actions';
import { fetchDataForLiveItemPage } from '@/redux/modules/itemPage/liveItemPage.actions';
import { fetchDataForUpcomingItemPage } from '@/redux/modules/itemPage/upcomingItemPage.actions';
import { AuctionStatus } from '@/types/LiveAuctionState';
import { isClientSide } from '@/utils/amplitude/analytics/core';
import { getFacetIds } from '@/utils/ItemPageUtils';
import { getAreUrlsEqual, getItemUrlFromSlug, getPriceResultsUrl, isPriceResultsPage } from '@/utils/urls';

import { getIsBot } from '../analytics';
import {
    getItemSummaryBuyNowAvailable,
    getItemSummaryCatalogStatus,
    getItemSummarySlug,
    getItemSummarySlugWithLocation,
    getItemSummaryTitle,
} from '../item/summary/itemSummary.selectors';
import { loadItemCarousels } from '../itemCarousel/itemCarousel.actions';
import { getCarousels } from '../itemCarousel/itemCarousel.selectors';
import { fetchItemDetailsIfNeeded } from '../itemDetail';
import { fetchItemFacetsIfNeeded, getItemFacets } from '../itemFacets';
import { fetchSavedItemCountsIfNeeded } from '../savedItemCount';
import { fetchCategoryRelatedSearchTiles } from '../search/categoryRelatedSearches/categoryRelatedSearches.actions';
import { fetchTopSellersIfNeeded } from '../topRatedSellers';
import { FetchDataForItemPageParams } from './itemPage.types';

export const fetchDataForItemPage = createAsyncThunk<Promise<unknown>, FetchDataForItemPageParams>(
    'la/domain/itemPage/fetchDataForItemPage',
    async (
        {
            itemId,
            location,
            redirect,
            referrer = '',
            shouldUseBackfillAuctioneerLots,
            shouldUseMaximumLotsPerAuctioneer,
            shouldUseSignalsBoost,
            useNewSimilarItems,
            useScoutCategories,
        },
        { dispatch, getState }
    ) => {
        const isBot = getIsBot(getState());

        // Omit ItemModel `similarItems` on lot detail: recommendations use Search `similaritemsgroups`
        // plus per-id summaries; `similarItemsURL` is not consumed under `src/pages/Item`.
        await Promise.all([
            dispatch(
                fetchItemSummaries({
                    identifier: 'item-page-main',
                    itemIds: [itemId],
                })
            ),
            dispatch(fetchItemFacetsIfNeeded([itemId])),
            dispatch(fetchItemDetailsIfNeeded([itemId])),
            dispatch(fetchSavedItemCountsIfNeeded([itemId])),
            dispatch(fetchTopSellersIfNeeded()),
        ]);

        // Fetch category related searches after itemFacets are loaded
        const facets = getItemFacets(getState(), itemId);
        const categoryIds = getFacetIds(facets);
        if (categoryIds.length) {
            dispatch(fetchCategoryRelatedSearchTiles({ categoryIds }));
        }

        const isClient = isClientSide();
        const stateAfterLoads = getState();
        const isLoggedInAfterLoads = isUserLoggedIn(stateAfterLoads);
        const catalogStatusAfterLoads = getItemSummaryCatalogStatus(stateAfterLoads, itemId);
        const buyNowAfterLoads = getItemSummaryBuyNowAvailable(stateAfterLoads, itemId);
        const slugAfterLoads = getItemSummarySlug(stateAfterLoads, itemId);
        const liveAuctioneersReferrer = referrer.includes('liveauctioneers.com');

        // Logged-out ended lots SSR redirect to /price-result/; skip similaritemsgroups + carousel summaries (ghost work).
        // Bots still load carousels for SSR HTML that may be served without redirect.
        const skipSsrItemPageCarouselForPriceResultRedirect =
            !isClient &&
            !isBot &&
            catalogStatusAfterLoads === AuctionStatus.Done &&
            Boolean(slugAfterLoads) &&
            !isLoggedInAfterLoads &&
            !liveAuctioneersReferrer &&
            !buyNowAfterLoads;

        // Fetch carousel data and item summaries for SSR (or client-side bot path)
        if ((!isClient || isBot) && !skipSsrItemPageCarouselForPriceResultRedirect) {
            await dispatch(
                loadItemCarousels({
                    itemId,
                    shouldUseBackfillAuctioneerLots,
                    shouldUseMaximumLotsPerAuctioneer,
                    shouldUseSignalsBoost,
                    useNewSimilarItems,
                    useScoutCategories,
                })
            );

            const carouselState = getState();
            const carousels = getCarousels(carouselState, itemId);
            const carouselItemIds: number[] = [];

            carousels.forEach((carousel) => {
                if (carousel.itemIds && carousel.itemIds.length > 0) {
                    carouselItemIds.push(...carousel.itemIds);
                }
            });

            if (carouselItemIds.length > 0) {
                await dispatch(
                    fetchItemSummaries({
                        identifier: 'item-page-carousel-items-ssr',
                        itemIds: carouselItemIds,
                    })
                );
            }
        }

        const state = getState();
        const isLoggedIn = isUserLoggedIn(state);
        const title = getItemSummaryTitle(state, itemId);
        const catalogStatus = getItemSummaryCatalogStatus(state, itemId);
        const buyNow = getItemSummaryBuyNowAvailable(state, itemId);
        const slug = getItemSummarySlug(state, itemId);
        const slugWithLocation = getItemSummarySlugWithLocation(state, itemId);

        // These redirects we're only going to do on the server so only when window is undefined.
        if (!isClient) {
            const { pathname, search } = location;
            const itemUrl = getItemUrlFromSlug(itemId, slugWithLocation);

            // Handle the price-results redirect
            if (catalogStatus === AuctionStatus.Done && slug && !isLoggedIn && !liveAuctioneersReferrer && !buyNow) {
                return redirect(getPriceResultsUrl(slug), true);
            }

            // Handle the bad url redirect where the itemId is good but the item title is not
            if (!getAreUrlsEqual(pathname, itemUrl) && !isPriceResultsPage(location.pathname) && title) {
                // goodUrl is found, return a permanent redirect with the better url
                if (search) {
                    return redirect(`${itemUrl}${search}`, true);
                }
                return redirect(itemUrl, true);
            }
        }

        if (catalogStatus === AuctionStatus.Done) {
            return dispatch(fetchDataForEndedItemPage({ itemId }));
        } else if (catalogStatus === 'online') {
            return dispatch(fetchDataForUpcomingItemPage({ itemId }));
        } /* the other case is catalogStatus === 'live' which is handled here */ else {
            return dispatch(
                fetchDataForLiveItemPage({
                    itemId,
                    shouldUseBackfillAuctioneerLots,
                    shouldUseMaximumLotsPerAuctioneer,
                    shouldUseSignalsBoost,
                    useNewSimilarItems,
                    useScoutCategories,
                })
            );
        }
    }
);
